import { computed, unref } from 'vue';
import { siteContactPublicRef } from '@/composables/siteContactState';
import logoPrimary from '@/assets/images/logo.png';
import logoWhite from '@/assets/images/logo-white.png';

export const ACCENT_MODES = Object.freeze({
  PRIMARY: 'primary',
  WHITE: 'white',
});

const STORAGE_KEY = 'storefront_accent_mode';

function pickConfig() {
  const value = siteContactPublicRef.value;
  return value && typeof value === 'object' ? value : null;
}

export function resolveStorefrontAccentMode(config) {
  const mode = String(config?.storefront_accent_mode || '').trim().toLowerCase();
  return mode === ACCENT_MODES.WHITE ? ACCENT_MODES.WHITE : ACCENT_MODES.PRIMARY;
}

export function applyStorefrontAccentMode(mode) {
  const normalized = mode === ACCENT_MODES.WHITE ? ACCENT_MODES.WHITE : ACCENT_MODES.PRIMARY;

  if (normalized === ACCENT_MODES.WHITE) {
    document.documentElement.setAttribute('data-accent', ACCENT_MODES.WHITE);
  } else {
    document.documentElement.removeAttribute('data-accent');
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, normalized);
  } catch {
    // ignore storage failures
  }
}

export function bootstrapStorefrontThemeFromStorage() {
  if (typeof window === 'undefined') return;

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === ACCENT_MODES.WHITE) {
      document.documentElement.setAttribute('data-accent', ACCENT_MODES.WHITE);
    }
  } catch {
    // ignore storage failures
  }
}

export function applyStorefrontThemeFromConfig(config) {
  if (config === undefined) return;
  applyStorefrontAccentMode(resolveStorefrontAccentMode(config));
}

export function resolveStorefrontLogoSrc(config, mode = resolveStorefrontAccentMode(config)) {
  if (mode === ACCENT_MODES.WHITE) {
    const url = String(config?.store_logo_alt_url || '').trim();
    return url || logoWhite || logoPrimary;
  }
  const url = String(config?.store_logo_url || '').trim();
  return url || logoPrimary;
}

export function useStorefrontTheme(overrides = {}) {
  const accentMode = computed(() => {
    const forced = unref(overrides.accentMode);
    if (forced === ACCENT_MODES.WHITE || forced === ACCENT_MODES.PRIMARY) {
      return forced;
    }
    const config = unref(overrides.config) ?? pickConfig();
    return resolveStorefrontAccentMode(config);
  });

  const logoSrc = computed(() => {
    const config = unref(overrides.config) ?? pickConfig();
    return resolveStorefrontLogoSrc(config, accentMode.value);
  });

  const isWhiteAccent = computed(() => accentMode.value === ACCENT_MODES.WHITE);

  return {
    accentMode,
    logoSrc,
    isWhiteAccent,
    ACCENT_MODES,
  };
}
